import { match } from 'ts-pattern'
import { z } from 'zod'
import type { AppScanWebhookAuthMethod } from '../../../utils/api-client/api-schemas.ts'
import {
  type ConnectionWebhookRouteType,
  type CreatableIntegrationType,
  getHasConnectionWebhookRoute,
  getHasWebhookHandoff,
} from './integration-instance-utils.ts'

/**
 * The `webhooks` HAL link on a Sonar or AppScan instance, holding the absolute URL that tool must
 * call. The URL names the instance by the id the platform generates on insert, which is why it can
 * only be read back off a response and is never composed from this page's own origin.
 *
 * The contract declares this link: `SonarIntegrationLinks` (ISS-8743) and `AppScanIntegrationLinks`
 * (ISS-8749) each require a `webhooks` member, and each replaces `ToolIntegrationLinks` on its own
 * response. So a parsed `Integration` of either type always has one. This structural reader survives
 * only for the create response, which `usePostIntegration` types as `unknown` — the drawer hands the
 * URL out immediately after a save, before anything has parsed the body into an `Integration`.
 */
const WebhookLinkCarrierSchema = z
  .object({
    _links: z
      .object({
        webhooks: z.object({ href: z.string().min(1) }),
      })
      .loose(),
  })
  .loose()

/**
 * The webhook URL an instance reports, or `undefined` when it reports none. Takes an `unknown` so it
 * reads both a create response — which the mutation types that way — and a parsed `Integration`.
 */
export const getWebhookUrl = (integrationOrResponse: unknown): string | undefined => {
  const parsed = WebhookLinkCarrierSchema.safeParse(integrationOrResponse)
  return parsed.success ? parsed.data._links.webhooks.href : undefined
}

/**
 * The chooser's own labels are `secret` and `basic`; the contract's `AppScanWebhookAuthMethod` names
 * the same two choices `shared-secret` and `basic-auth`. Translated in one place, because the
 * platform accepts a credential only when the method it belongs to is the one named on the request
 * and rejects the other one's outright.
 */
export const getWebhookAuthMethod = (choice: WebhookAuthChoice): AppScanWebhookAuthMethod =>
  match(choice)
    .with('basic', () => 'basic-auth' as const)
    .with('secret', () => 'shared-secret' as const)
    .exhaustive()

export const WEBHOOK_URL_LABEL = 'Webhook URL'
export const WEBHOOK_URL_COPY_LABEL = 'Copy webhook URL'

/** Says what deleting the instance costs, on every screen that hands the URL out. */
export const getWebhookUrlHelperText = (toolLabel: string): string =>
  `This URL is tied to this instance. If you delete the instance, the URL stops working and you will need to reconfigure ${toolLabel} with a new one.`

/**
 * What the delete confirmation says about inbound deliveries, or `undefined` for a type Pixee only
 * polls. Sits beside {@link getWebhookUrlHelperText}, which states the same fact for the two tools
 * whose URL the Setup and Manage drawers hand out with helper text attached: both exist because the
 * route names the instance by the id the platform generated for it, so the URL dies with the
 * instance and a replacement is reachable at a different one. The three providers' URLs are shown in
 * the Manage drawer without that helper text, so for them this modal is the only place it is said.
 *
 * Two copies rather than one, because the delivering side differs. A tool was pointed at Pixee to
 * send its results, and the user is told what stops arriving. A source provider delivers only if the
 * user created a hook in the provider's own product, and nothing here reports whether they did:
 * Azure's `webhook_configured` and GitHub's required `webhook_secret` say that Pixee holds a
 * credential — this side of the exchange — and GitLab reports nothing at all. So the provider copy
 * is written to hold either way, and what it promises is the part that certainly breaks: the URL.
 */
export const getWebhookDeleteWarning = ({
  type,
  toolLabel,
}: {
  type: CreatableIntegrationType
  toolLabel: string
}): string | undefined => {
  if (getHasWebhookHandoff(type)) {
    return `${toolLabel} is configured to send results to this instance's webhook URL. Deleting the instance stops those deliveries; to keep receiving results, connect a replacement instance and update the URL in ${toolLabel}.`
  }
  if (getHasConnectionWebhookRoute(type)) {
    return `Deleting this connection makes its webhook URL stop working, so any ${toolLabel} ${getProviderHookNoun(type)} pointing at it stops delivering. A replacement connection gets a different URL, which you have to set in ${toolLabel}.`
  }
  return undefined
}

/**
 * What the provider's own product calls the thing the user creates there. Azure DevOps has no
 * feature named "webhook" — its settings call it a service hook, the term this repo's contract also
 * uses for it (`AzureIntegrationResponse.webhook_user`: "the basic auth an Azure DevOps service hook
 * must present") — so copy that says "webhook" sends an Azure user looking for a screen that does
 * not exist. Pixee's own side keeps the one name it is labelled with, "Webhook URL".
 */
const getProviderHookNoun = (type: ConnectionWebhookRouteType): string =>
  match(type)
    .with('azure', () => 'service hook')
    .with('gitlab', 'github', () => 'webhook')
    .exhaustive()

/**
 * Shown beside a Sonar webhook URL that no secret protects. A statement of fact rather than an
 * error: the user chose this by leaving the secret blank, so nothing is marked invalid and nothing
 * is disabled. It exists so the choice is visible on the screen that hands out the URL, and not only
 * on the form where it was made.
 */
export const UNPROTECTED_WEBHOOK_HELPER_TEXT =
  'This URL is not protected — Pixee accepts any call to it. Add a webhook secret to verify that calls come from your Sonar.'

export type WebhookAuthChoice = 'secret' | 'basic'

/**
 * What the handoff needs to know about the instance just created, which is a different fact per
 * tool: Sonar's webhook secret is optional and its absence is worth saying out loud, while AppScan
 * always authenticates and only the chosen method matters. Discriminated so neither tool is handed a
 * field that means nothing to it — a Sonar handoff has no auth method to report, and passing one
 * left a reader unable to tell which of the two fields each branch actually used.
 */
export type WebhookHandoffSubject =
  { type: 'sonar'; hasWebhookSecret: boolean } | { type: 'appscan'; webhookAuthChoice: WebhookAuthChoice }

/**
 * What the user still has to do in the tool itself. Sonar's secret step is omitted when the instance
 * was saved without one, so the list renumbers rather than showing a step the user cannot complete —
 * and no copy invites them to set a secret in Sonar alone, which would have Sonar sign calls Pixee
 * does not check.
 */
export const getWebhookHandoffSteps = (subject: WebhookHandoffSubject): string[] =>
  match(subject)
    .with({ type: 'sonar' }, ({ hasWebhookSecret }) =>
      [
        'In SonarQube, open Administration → Configuration → Webhooks (global, or per project for SonarQube Server).',
        'Create a webhook with the URL above.',
        hasWebhookSecret ? 'Paste the webhook secret you entered into the Secret field.' : undefined,
        'Save. Results from your next analysis will appear in Pixee.',
      ].filter((step): step is string => step !== undefined)
    )
    .with({ type: 'appscan' }, ({ webhookAuthChoice }) => [
      'In AppScan, open the webhook settings for your application or organization.',
      'Set the URL to the Webhook URL above.',
      webhookAuthChoice === 'secret'
        ? "Include your webhook secret in the request body's webhook_secret field."
        : 'Use the username and password you entered.',
      'Save. Results from your next scan will appear in Pixee.',
    ])
    .exhaustive()
